/**
 * Co-located incident clustering core (tracker #415, Phase 6b — "co-located
 * legibility"). Pure, framework-free (NO Cesium) so it's unit-testable and reused
 * straight by MapView's co-location render path.
 *
 * THE PROBLEM (§E-sim prerequisite): when N≥2 ACT incidents — the seven families
 * MapView's `incidentRefOf` recognises (hr/fc/cap/sm/ice/fl/ou) — pile onto the same screen
 * pixel, the operator can't see how many are stacked, can't tell them apart, and
 * can't click each. The rejected #390 click-cycle stepped through the stack BLIND
 * (no count, no list). This module instead GROUPS the stack so MapView can drop an
 * honest "+N incidents here" badge at the centroid (self-revealing) and open a
 * disambiguation list on click (each incident individually selectable).
 *
 * Screen-SPACE, not ground-space: the camera clusterer ({@link ./cameraCluster})
 * groups in ground metres because cameras cluster by physical site; co-location is
 * a SCREEN collision (two incidents 80 km apart collide at country zoom, separate
 * on zoom-in), so this clusters by window-pixel distance. The collapse-on-zoom
 * behaviour is automatic: as the operator zooms in, the projected points move
 * apart, fall outside the pixel radius, and the cluster dissolves on the next
 * recompute — no timeout (the #390 timeout belonged to the invisible cycle state).
 *
 * Icon one-swing #564 (founder re-ruling 2026-07-06): the numbered "+N" stack badge is
 * RETIRED — co-located markers now render SIDE BY SIDE around the shared midpoint. This is
 * unlocked by the same swing converting every hazard family to a BILLBOARD (discGlyphComposite):
 * Cesium billboards DO take a screen `pixelOffset` (the 2026-06-23 "impossible" note was for
 * `point` graphics). {@link fanOffsets} below computes the deterministic horizontal offsets;
 * MapView applies them to each co-located member's primary billboard. This module still only
 * detects the stacks + computes the offsets (pure); MapView owns the Cesium application + the
 * click-disambiguation picker (kept as the backstop).
 *
 * Round-2 band fix (founder NIGHT ruling 3, 2026-07-06): the side-by-side fan is a CLOSE-ZOOM
 * declutter ONLY — it runs solely below {@link FAN_MAX_CAMERA_HEIGHT_M}, caps at
 * {@link COLOCATED_FAN_MAX_MEMBERS} members, and its offsets stay ≤ ~1.2 marker widths. See
 * {@link computeFanOffsets} for the single pure decision that closes the ocean-band class.
 */
import { ICON_LADDER } from './styles/responseLevel';

/** A single ACT incident's screen position, for one recompute. */
export interface ColocatedPoint {
  /** Stable per-incident key (family tag + feature id), e.g. `highRiskHotspot:uuid`.
   *  Minted by MapView's `incidentRefOf`, so membership has ONE source of truth. */
  key: string;
  /** Window x in CSS pixels (Cesium `worldToWindowCoordinates`). */
  x: number;
  /** Window y in CSS pixels. */
  y: number;
  /**
   * Sort weight for the disambiguation list + any badge truncation: HIGHER is more
   * urgent (e.g. response level, with a family tiebreak). The clusterer is agnostic
   * to how it's computed — it only orders members by it (desc), key asc to break
   * ties deterministically.
   */
  priority: number;
}

/** A detected stack of co-located ACT incidents. A one-member "cluster" is returned
 *  too (uniform shape, mirroring {@link ./cameraCluster.clusterCameras}); the caller
 *  filters to {@link ColocatedCluster.count} ≥ 2 for the badge + panel. */
export interface ColocatedCluster {
  /** Stable id from the sorted member keys, so an unchanged grouping re-keys
   *  identically across recomputes (lets the renderer diff badge entities). A
   *  single-member cluster's id is just that incident's key. */
  id: string;
  /** Centroid in window pixels (mean of member x/y) — where the "+N" badge sits. */
  x: number;
  y: number;
  /** Member keys, ordered by priority DESC then key ASC (most-urgent first for the
   *  disambiguation list). Length === {@link count}. */
  members: string[];
  /** Member count (N). The badge shows this verbatim — honest, unlike #390. */
  count: number;
}

/**
 * Default screen-pixel collision radius — two ACT markers whose projected centres
 * are within this are "co-located". ≈ the resting marker diameter / the camera
 * cluster pixel radius (`CLUSTER_PIXEL_RADIUS = 34` in MapView): markers closer
 * than ~one diameter visually overlap. MapView may override per the active marker
 * sizes; kept here so the unit tests pin a concrete default.
 */
export const COLOCATED_PIXEL_RADIUS = 30;

/** Euclidean window-pixel distance between two points. */
export function pixelDistance(
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  return Math.sqrt(dx * dx + dy * dy);
}

interface Accumulator {
  members: ColocatedPoint[];
  sumX: number;
  sumY: number;
}

/** Order members most-urgent-first: priority DESC, then key ASC (stable + deterministic). */
function orderByPriority(members: readonly ColocatedPoint[]): ColocatedPoint[] {
  return [...members].sort((a, b) =>
    b.priority !== a.priority ? b.priority - a.priority : a.key < b.key ? -1 : a.key > b.key ? 1 : 0,
  );
}

/**
 * Group ACT incident screen-points into co-located clusters at the current zoom.
 *
 * Greedy leader clustering (mirrors {@link ./cameraCluster.clusterCameras}): points
 * are visited in a deterministic order (sorted by key) and each joins the first
 * existing cluster whose running centroid is within `radiusPx`, else seeds a new
 * one. Stable + independent of the input array order, so the badge grouping never
 * flickers between recomputes for an unchanged view.
 *
 * Returns ALL clusters (one-member included) for a uniform shape; the caller keeps
 * those with `count >= 2`.
 */
export function groupColocated(
  points: readonly ColocatedPoint[],
  radiusPx: number = COLOCATED_PIXEL_RADIUS,
): ColocatedCluster[] {
  const sorted = [...points].sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  const accs: Accumulator[] = [];

  for (const pt of sorted) {
    let placed = false;
    for (const acc of accs) {
      const cx = acc.sumX / acc.members.length;
      const cy = acc.sumY / acc.members.length;
      if (pixelDistance(pt.x, pt.y, cx, cy) <= radiusPx) {
        acc.members.push(pt);
        acc.sumX += pt.x;
        acc.sumY += pt.y;
        placed = true;
        break;
      }
    }
    if (!placed) accs.push({ members: [pt], sumX: pt.x, sumY: pt.y });
  }

  return accs.map((acc) => {
    const ordered = orderByPriority(acc.members);
    const keys = ordered.map((m) => m.key);
    const multi = keys.length > 1;
    const idKeys = [...keys].sort();
    return {
      id: multi ? `coloc:${idKeys.join('|')}` : keys[0]!,
      x: acc.sumX / acc.members.length,
      y: acc.sumY / acc.members.length,
      members: keys,
      count: keys.length,
    };
  });
}

/** Horizontal spacing (CSS px) between adjacent fanned markers — icon one-swing #564.
 *  DERIVED from the size ladder (not hand-written) so it always tracks the current RL4 disc: at
 *  ~1.1× the resting RL4 diameter the fanned discs sit edge-to-edge (a small gap), not overlapping,
 *  and — with the {@link COLOCATED_FAN_MAX_MEMBERS}=3 cap — the outermost offset (±one step) stays
 *  ≤ the founder's "≤ ~1.2 marker widths" clamp (NIGHT ruling, 2026-07-06). When the founder
 *  eyeballs a new RL4 size the step follows automatically. The fan collapses on zoom-in when the
 *  true positions separate past the collision radius. */
export const COLOCATED_FAN_STEP = Math.round(1.1 * ICON_LADDER[4].baseSize);

/**
 * Camera-height ceiling (metres) at/above which the side-by-side co-location fan is DISABLED.
 *
 * Founder NIGHT ruling 3 (2026-07-06): side-by-side is a CLOSE-ZOOM declutter ONLY — at
 * country / regional / national zoom co-located items render at their TRUE positions (overplotting
 * where they collide; the shipped click-picker discloses the stack on click), NEVER a wide-area
 * geographic spread. (The ruling also says "collapse to one marker (+count idiom)"; whether a
 * PASSIVE national-zoom count badge should ALSO show is a FLAGGED founder decision, NOT decided
 * here — #564 retired the numbered-stack badge, so re-adding one needs sign-off. See the run-log.)
 *
 * WHY THIS GATE IS LOAD-BEARING (round-2 measured cause, not a guess): {@link groupColocated}
 * clusters by SCREEN-pixel distance, so at national zoom in-Canada markers hundreds of km apart
 * collapse into ONE screen-cluster; the horizontal {@link fanOffsets} pixel fan (offsetY always
 * 0) then strings that cluster along a CONSTANT screen-Y — a constant-latitude BAND that runs off
 * Canada's screen extent onto the Pacific/Atlantic. The billboards' geographic ANCHORS stay
 * in-Canada, so the source-coordinate Canada guard (canadaScope.ts) is structurally blind to it —
 * which is why round 1's guard did not stop the band. Live measurement 2026-07-06: 277 (later 185,
 * data churns) in-Canada RL≥3 hotspots (ZERO out-of-Canada) projected to a national view → a
 * 76-member screen-cluster → a 2250 px fan reaching x=−565 (Pacific) and x=1685 (Atlantic).
 *
 * CEILING VALUE = 2.0e5 m — the discCountryScale NEAR bound (anomalyMarker.ts), the codebase's own
 * "disc at full size / close inspection" boundary. Chosen over the wider hide-at-country boundary
 * (2.0e6) because at heights just under 2e6 a capped fan still displaces a marker ~100+ km (a
 * province-scale mini-band); at ≤2e5 a 30 px screen collision is a genuine ≤~5 km co-location and
 * the max fan displacement is a few km — a true close-inspection declutter, never a geographic
 * spread (adversary-verified 2026-07-06). A self-contained literal (this module is framework-free);
 * the value is pinned by a test.
 */
export const FAN_MAX_CAMERA_HEIGHT_M = 2.0e5;

/**
 * True when the camera is close enough (close-inspection zoom) for the side-by-side fan to run. At
 * or above the ceiling the fan is DISABLED and co-located markers render at their true positions.
 * A non-finite height is treated as "too high" (no fan) so a degenerate camera can never spray the
 * band.
 */
export function fanEnabledAtHeight(cameraHeightM: number): boolean {
  return Number.isFinite(cameraHeightM) && cameraHeightM < FAN_MAX_CAMERA_HEIGHT_M;
}

/**
 * Member cap for the fan (defence in depth behind {@link fanEnabledAtHeight}, and the mechanism
 * that keeps every offset within the founder's "≤ ~1.2 marker widths" clamp). A symmetric fan of
 * 3 places its outermost members at exactly ±one {@link COLOCATED_FAN_STEP} (~1.1 marker widths);
 * a 4th would push past the clamp. So a cluster of 2–3 fans side-by-side, and a cluster of 4+ is
 * NOT fanned — its members stay overplotted at their true positions and the click-picker discloses
 * the count, rather than spreading a wider row. */
export const COLOCATED_FAN_MAX_MEMBERS = 3;

/**
 * The COMPLETE side-by-side fan decision as ONE pure function — the structural closure of the
 * round-2 ocean-band class. Given the visible incident screen-points and the current camera
 * height, returns the horizontal pixelOffset (CSS px) each member's billboard should get.
 *
 * Invariants this function guarantees (pinned by coLocatedClusters.test.ts):
 *   • At country/national zoom ({@link fanEnabledAtHeight} false) the result is EMPTY — every
 *     billboard's rendered position then ≡ its source coordinates, so NO co-located spread can
 *     string markers across a constant screen-Y into the oceans. This is the permanent closure
 *     of the founder's constant-latitude band (round 2, 2026-07-06).
 *   • Below the ceiling it groups screen-collisions and fans clusters of 2..{@link
 *     COLOCATED_FAN_MAX_MEMBERS} symmetric around the shared midpoint; a cluster past the member
 *     cap is left un-fanned (overplot + the click-picker idiom), never spread into a wide row.
 *
 * MapView applies each returned dx as `billboard.pixelOffset(dx, 0)` and resets any previously
 * fanned key absent from the result. Pure + deterministic (offsetY is always 0; see fanOffsets).
 */
export function computeFanOffsets(
  points: readonly ColocatedPoint[],
  cameraHeightM: number,
  radiusPx: number = COLOCATED_PIXEL_RADIUS,
): Map<string, number> {
  const out = new Map<string, number>();
  if (!fanEnabledAtHeight(cameraHeightM)) return out; // country/national zoom → no fan (band fix)
  const stacks = groupColocated(points, radiusPx).filter(
    (c) => c.count >= 2 && c.count <= COLOCATED_FAN_MAX_MEMBERS,
  );
  for (const cluster of stacks) {
    const offsets = fanOffsets(cluster.count);
    cluster.members.forEach((key, i) => out.set(key, offsets[i]!));
  }
  return out;
}

/**
 * Deterministic horizontal fan offsets (CSS px) for `count` co-located markers, CENTERED on
 * the shared midpoint (founder re-ruling 2026-07-06 — side by side, not a numbered stack).
 * Member `i` (in the cluster's priority order) sits at `(i - (count-1)/2) * step`, so the row
 * is symmetric around 0: count 1 → [0]; count 2 → [-step/2, +step/2]; count 3 → [-step, 0, +step].
 * Pure + deterministic (the caller passes members already priority-ordered by {@link groupColocated}),
 * so the fan never flickers between recomputes for an unchanged stack. offsetY is always 0 (a pure
 * horizontal fan). Returns [] for count ≤ 0.
 */
export function fanOffsets(count: number, step: number = COLOCATED_FAN_STEP): number[] {
  const out: number[] = [];
  for (let i = 0; i < count; i++) out.push((i - (count - 1) / 2) * step);
  return out;
}

/** The disambiguation-list head shown by default before scrolling — top members by
 *  priority. The panel itself can scroll the rest; this is a courtesy cap for any
 *  caller that wants a bounded preview (and the honest tail count). */
export const COLOCATED_LIST_HEAD = 8;

/**
 * Split an ordered member list into a bounded head + the honest overflow count.
 * `members` is assumed already priority-ordered (as {@link groupColocated} returns).
 * Returns the first `cap` members and how many remain (never negative). The badge
 * shows the FULL count; this is only for a caller that caps a preview list.
 */
export function splitOverflow(
  members: readonly string[],
  cap: number = COLOCATED_LIST_HEAD,
): { head: string[]; overflow: number } {
  if (members.length <= cap) return { head: [...members], overflow: 0 };
  return { head: members.slice(0, cap), overflow: members.length - cap };
}
